import { getCookie } from "./clientStorageActions";

export const getIdentifier = async (): Promise<string | undefined> => {
  const cookieItems = document.cookie ? document.cookie.split(";") : undefined;
  if (!cookieItems) return undefined;

  const deviceIdCookie = cookieItems
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith("deviceId="));

  return deviceIdCookie?.split("=")?.[1];
};

interface FetchOptions extends RequestInit {
  params?: Record<string, any>;
  locale?: string;
  'App-Platform'?: string;
}

const createApiClient = () => {
  let baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "";

  async function request(
    url: string,
    options: FetchOptions = {},
    baseUrlOverride?: string,
    headers?:any
  ) {
    const apiBase = baseUrlOverride || baseUrl || "";
    let fullUrl = apiBase + url; // define early so params can append to it
    options.headers = {
      "Content-Type": "application/json",
    };
    // Copy incoming params
    const params: Record<string, any> = {
      ...(options.params || {}),
    };
    if (typeof window !== "undefined") {
      const identifier = await getIdentifier();
      if (!fullUrl?.includes('/device/register') && (!identifier || !identifier.trim())) {
        return { error: true, message: "Missing identifier" };
      }
      params.identifier = identifier;
      // Logic to determine if COD mode should be added
      const targetUrls = [
        "/medicines/replace-all",
        "/medicines/replace-or-add",
        "/order/[order_id]/re-order",
      ];
      const isCartPostOrPut =
        url === "/cart/items" &&
        options.method &&
        !["DELETE", "GET"].includes(options.method.toUpperCase());

      if ((targetUrls.includes(url) || isCartPostOrPut) && !params.mode) {
        params.mode = "COD";
      }

      options.headers.Authorization = "Bearer " + getCookie("accessToken");
    }
    options.headers.lang = headers?.lang ?? getCookie("lang") ?? "en";
    options.headers['App-Platform'] = 'web';
    if (Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          // Remove undefined/null
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();

      if (queryString) {
        fullUrl += (fullUrl.includes("?") ? "&" : "?") + queryString;
      }
    }
    // Ensure POST/PUT/PATCH body is stringified
    if (
      options.body &&
      typeof options.body !== "string" &&
      ["POST", "PUT", "PATCH"].includes((options.method || "").toUpperCase())
    ) {
      options.body = JSON.stringify(options.body);
    }

    try {
      const res = await fetch(fullUrl, options);

      // Try to parse JSON or fallback to text
      let data;
      try {
        data = await res.json();
      } catch {
        data = await res.text();
      }

      if (!res.ok) {
        throw {
          ok: false,
          url: fullUrl,
          method: options.method || "GET",
          status: res.status,
          statusText: res.statusText,
          response: { data },
        };
      }

      return { data, status: res.status, statusText: res.statusText };
    } catch (err) {
      throw err;
    }
  }

  return {
    get: (url: string, options?: FetchOptions, baseUrlOverride?: string , headers?:any) =>
      request(url, { ...options, method: "GET" }, baseUrlOverride,headers),

    post: (url: string, body?: any, baseUrlOverride?: string,  headers?:any) =>
      request(url, { method: "POST", body }, baseUrlOverride, headers),

    put: (url: string, body?: any, baseUrlOverride?: string,  headers?:any) =>
      request(url, { method: "PUT", body }, baseUrlOverride, headers),

    delete: (url: string, options?: FetchOptions, baseUrlOverride?: string,  headers?:any) =>
      request(url, { ...options, method: "DELETE" }, baseUrlOverride, headers),
  };
};

export default createApiClient;
